import React, { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import { routes } from '~src/routingConfig';
import { translateContent } from '~src/services/translationServices';
import { Uuid } from '~src/services/uuid';
import AvatarsRow from '~src/shared/avatarsRow/AvatarsRow';
import ProgressBar from '~src/shared/ProgressBar';
import { ElementWrapper, UpdateButtonContainer } from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import { ownerToSegment } from '~src/modules/okrs/manageObjectives/shared/utils';
import { LastRow, ListElementWrapper, Title } from '~src/modules/okrs/manageObjectives/edit/editListItem.sc';
import { SubObjectiveItem } from '~src/store/okrs/interfaces';
import RemovableItem from '~src/modules/okrs/manageObjectives/create/RemovableItem';
import { Weighting } from '~src/modules/okrs/manageObjectives/shared/Weighting';

interface OwnProps {
  objective: SubObjectiveItem;
  readonly: boolean;
  unlinkSubobjective: (objectiveId: string) => void;
  setSelectedSubobjective?: (objective: SubObjectiveItem) => void;
  creationMode?: boolean;
}

type Props = OwnProps & RouteComponentProps<any>;

const EditSubobjectiveItem: FC<Props> = props => {
  const { objective } = props;
  const [t] = useTranslation();

  // NOTE I'm using nulled creator uuid as proxy for
  // "we're allowed to read the relation, but not allowed to read the objective details"
  const accessible = props.objective.creator_id !== Uuid.nullValue().toString();

  const stopPropagation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (props.setSelectedSubobjective) {
      props.setSelectedSubobjective(props.objective);
    }
  };

  const linkIfNavigationAllowed = (inner: ReactElement): ReactElement => {
    if (!accessible) {
      return inner;
    }

    let click = (_: React.MouseEvent<HTMLDivElement>) => {
      props.history.push(routes.okrs.objectiveDetails(props.objective.id));
    };
    if (props.creationMode) {
      click = (_: React.MouseEvent<HTMLDivElement>) => {
        if (props.setSelectedSubobjective) {
          props.setSelectedSubobjective(objective);
        }
      };
    }

    return (
      <div style={{ width: '100%' }} onClick={click}>
        {inner}
      </div>
    );
  };

  const removableIfCreationMode = (inner: ReactElement): ReactElement =>
    props.creationMode ? <RemovableItem remove={unlinkSubobjective}>{inner}</RemovableItem> : inner;

  const unlinkSubobjective = () => {
    if (props.unlinkSubobjective !== undefined) {
      props.unlinkSubobjective(objective.id);
    }
  };

  return (
    <ListElementWrapper>
      {removableIfCreationMode(
        linkIfNavigationAllowed(
          <ElementWrapper $selectable={accessible}>
            <Title>{translateContent(objective.name)}</Title>
            <div>
              <span data-translation="x.okrs.progress.indicator">
                {t('x.okrs.progress.indicator', { progress: objective.progress })}
              </span>
              {!props.creationMode && (
                <ProgressBar height={5} width="100%" staticProgress progress={objective.progress} />
              )}
            </div>
            <LastRow>
              <AvatarsRow userData={(objective.owners || []).map(ownerToSegment)} />
              <Weighting weighting={objective.weighting} locked={objective.weighting_locked} />
            </LastRow>
            {!!props.setSelectedSubobjective && !props.creationMode && (
              <UpdateButtonContainer>
                {
                  <PrimaryButton
                    data-translation="x.objective.sub-objectives.item.button.edit.text"
                    onClick={stopPropagation}
                  >
                    {t('x.objective.sub-objectives.item.button.edit.text')}
                  </PrimaryButton>
                }
              </UpdateButtonContainer>
            )}
          </ElementWrapper>,
        ),
      )}
    </ListElementWrapper>
  );
};

export default withRouter(EditSubobjectiveItem);
